import { captureException } from "@sentry/react-router";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
  Link,
} from "react-router";
import type { LinksFunction, LoaderFunction } from "react-router";
import { Theme, Card, Heading, Text, Button, Flex } from "@radix-ui/themes";

import { useTheme } from "./routes/resources/theme-switch";
import { ClientHintCheck, getHints } from "./utils/client-hints";
import { getSystemType } from "./utils/environment.server";
import "./tailwind.css";

export const loader: LoaderFunction = async ({ request }) => {
  const systemType = await getSystemType();
  return {
    requestInfo: {
      hints: getHints(request),
      systemType,
    },
  };
};

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href:
      "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
];

function useAppTheme() {
  try {
    return useTheme();
  } catch {
    return "light";
  }
}

export function Layout({ children }: { children: React.ReactNode }) {
  const theme = useAppTheme();
  return (
    <html lang="en" className={theme}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <ClientHintCheck />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  let message = "Error";
  let details = "Something went wrong";

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details;
  }

  captureException(error);

  return (
    <Theme appearance="inherit" accentColor="blue" radius="small">
      <Flex align="center" justify="center" className="h-screen">
        <Card variant="surface" className="max-w-sm w-full shadow-lg p-6">
          <Heading size="6" weight="bold" mb="4" align="center">
            {message}
          </Heading>
          <Text color="gray" size="3" mb="6" align="center" as="p">
            {details}
          </Text>
          <Button asChild className="w-full">
            <Link to="/dashboard">Go to Dashboard</Link>
          </Button>
        </Card>
      </Flex>
    </Theme>
  );
};

export default function App() {
  return (
    <Theme appearance="inherit" accentColor="blue" radius="small">
      <Outlet />
    </Theme>
  );
}
